import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api.ts'
import { useSession } from './session.tsx'
import { espelharNaSessao } from './sessao-em-cache.ts'


/**
 * Preferencia de LAYOUT da pessoa (largura de painel, o que fica aberto).
 *
 * Mora na CONTA (tb_member.member_layout_prefs) e nao no localStorage: a
 * preferencia e da pessoa, nao da maquina — a mesma largura vale no desktop e
 * no navegador, e dois colegas no mesmo computador nao herdam o ajuste um do
 * outro (localStorage e por origem).
 *
 * A tela nunca espera o servidor: o valor muda na hora (estado local) e a
 * gravacao vai depois, uma so vez por pausa de meio segundo — arrastar uma
 * borda dispara dezenas de mudancas e cada uma viraria um PATCH.
 */
export function usePreferenciaDeLayout<T extends string | number | boolean>(
  chave: string,
  padrao: T,
): [T, (valor: T) => void] {
  const sessao = useSession()
  const queryClient = useQueryClient()
  // vem do banco como JSON solto: só assume se o tipo bater com o padrão
  const guardadas = (sessao.layoutPrefs ?? {}) as Record<string, unknown>
  const salvo = guardadas[chave]
  const [valor, setValor] = useState<T>(() => (typeof salvo === typeof padrao ? (salvo as T) : padrao))

  /**
   * Quando a sessao chega (ou troca de pessoa) o valor gravado assume — mas so
   * enquanto esta tela ainda nao mexeu nele, senao a resposta de um /me antigo
   * desfaria o arraste que a pessoa acabou de fazer.
   */
  const tocado = useRef(false)
  useEffect(() => {
    if (tocado.current) return
    if (typeof salvo === typeof padrao) setValor(salvo as T)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salvo])

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendente = useRef<T | null>(null)

  const gravar = useCallback(() => {
    const valorFinal = pendente.current
    pendente.current = null
    timer.current = null
    if (valorFinal === null) return
    void api.auth.me.layout
      .$patch({ json: { prefs: { [chave]: valorFinal } } })
      // preferencia de conforto: falhar ao gravar nao pode atrapalhar a tela,
      // e a proxima mudanca tenta de novo
      .catch(() => {})
  }, [chave])

  // o que estiver pendente ao sair da tela vai agora, sem esperar a pausa
  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
        gravar()
      }
    }
  }, [gravar])

  const definir = useCallback(
    (novo: T) => {
      tocado.current = true
      setValor(novo)
      // já na hora: quem voltar à tela antes da pausa de meio segundo também
      // tem de encontrar esta escolha
      espelharNaSessao(queryClient, chave, novo)
      pendente.current = novo
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(gravar, 500)
    },
    [gravar, queryClient, chave],
  )

  return [valor, definir]
}

/**
 * Preferencia ESTRUTURADA da pessoa — o estado de uma tela inteira, nao um
 * numero: a tabela de Itens de Trabalho guarda aqui suas colunas (ordem,
 * largura, visibilidade) e por qual delas esta ordenada.
 *
 * Mora no mesmo lugar e sobe pela mesma rota da `usePreferenciaDeLayout`,
 * com a mesma pausa de meio segundo — arrastar a borda de uma coluna dispara
 * dezenas de mudancas e cada uma viraria um PATCH.
 *
 * `adotar` e o filtro de entrada: o valor vem do banco como JSON solto e pode
 * ser de uma versao anterior da tela (coluna que nao existe mais, campo que
 * mudou de nome). Quem chama decide o que fazer com isso e devolve `null`
 * quando nao da para aproveitar — ai vale o padrao, em vez de a tela quebrar
 * por causa de uma preferencia velha.
 */
export function usePreferenciaEstruturada<T extends Record<string, unknown>>(
  chave: string,
  padrao: T,
  adotar: (bruto: unknown) => T | null,
): [T, (valor: T) => void] {
  const sessao = useSession()
  const queryClient = useQueryClient()
  const guardadas = (sessao.layoutPrefs ?? {}) as Record<string, unknown>
  const salvo = guardadas[chave]

  // `adotar` muda de identidade a cada render (fecha sobre o catalogo da tela);
  // guardar a ultima evita refazer o estado inicial por causa disso
  const adotarRef = useRef(adotar)
  adotarRef.current = adotar

  const [valor, setValor] = useState<T>(() => adotarRef.current(salvo) ?? padrao)

  /**
   * Quando a sessao chega (ou troca de pessoa) o que estava gravado assume —
   * mas so enquanto esta tela ainda nao mexeu nele, senao a resposta de um /me
   * antigo desfaria o ajuste que a pessoa acabou de fazer.
   */
  const tocado = useRef(false)
  useEffect(() => {
    if (tocado.current) return
    const vindo = adotarRef.current(salvo)
    if (vindo) setValor(vindo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salvo])

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendente = useRef<T | null>(null)

  const gravar = useCallback(() => {
    const valorFinal = pendente.current
    pendente.current = null
    timer.current = null
    if (valorFinal === null) return
    void api.auth.me.layout
      .$patch({ json: { prefs: { [chave]: valorFinal } } })
      // preferencia de conforto: falhar ao gravar nao pode atrapalhar a tela,
      // e a proxima mudanca tenta de novo
      .catch(() => {})
  }, [chave])

  // o que estiver pendente ao sair da tela vai agora, sem esperar a pausa
  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
        gravar()
      }
    }
  }, [gravar])

  const definir = useCallback(
    (novo: T) => {
      tocado.current = true
      setValor(novo)
      // já na hora: quem voltar à tela antes da pausa de meio segundo também
      // tem de encontrar esta escolha
      espelharNaSessao(queryClient, chave, novo)
      pendente.current = novo
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(gravar, 500)
    },
    [gravar, queryClient, chave],
  )

  return [valor, definir]
}
